import { Link } from 'react-router-dom';

interface NodeInstanceLinkProps {
  readonly nodeInstanceId: number;
  /** The instance's definition name; the id stands in when it is unknown. */
  readonly nodeName: string | null;
  /**
   * The top-level instance it runs under: itself when it is top-level, null
   * when the caller does not know.
   */
  readonly rootNodeInstanceId: number | null;
}

/**
 * A node instance's name and id, the name linking to the Node Instances list.
 *
 * The id is always shown, as the instance viewer does: a name is the
 * definition's, so every instance of it shares it.
 *
 * With a known root, the list is narrowed to that root, with the bottom panel
 * open on this instance, so a flow step is shown in its flow. Without one it
 * falls back to filtering on the instance itself; `parentOnly=false` matters
 * there, because the list defaults to top-level instances only and a flow
 * step would land on an empty page.
 */
export const NodeInstanceLink = ({ nodeInstanceId, nodeName, rootNodeInstanceId }: NodeInstanceLinkProps) => (
  <>
    <Link to={nodeInstanceHref(nodeInstanceId, rootNodeInstanceId)}>{nodeName ?? `Instance ${nodeInstanceId}`}</Link>
    {nodeName !== null && <span className="text-muted"> ({nodeInstanceId})</span>}
  </>
);

/** As {@link NodeInstanceLink} reads, in plain text: for an instance named but not linked. */
export const nodeInstanceLabel = (nodeInstanceId: number, nodeName: string | null): string =>
  nodeName === null ? `Instance ${nodeInstanceId}` : `${nodeName} (${nodeInstanceId})`;

const nodeInstanceHref = (nodeInstanceId: number, rootNodeInstanceId: number | null): string => {
  if (rootNodeInstanceId === null) {
    return `/automation/node_instances?node_instance_id=${nodeInstanceId}&parentOnly=false&sortColumn=node_instance_id&sortOrder=desc&page=1&limit=100`;
  }
  return `/automation/node_instances?node_instance_id=${rootNodeInstanceId}&bottom_panel_id=${nodeInstanceId}`;
};
